import { ApolloLink, Observable } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';

import { networkStatusVar } from '../../context';

let healthCheckPromise: Promise<boolean> | null = null;
let reachabilityCheckVersion = 0;

const checkBackendHealth = async (): Promise<boolean> => {
  if (healthCheckPromise) return healthCheckPromise;

  const healthUrl = (import.meta.env.VITE_API_URI ?? '').replace('/graphql', '/health');

  healthCheckPromise = fetch(healthUrl, {
    method: 'GET',
    cache: 'no-store',
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      healthCheckPromise = null;
    });

  return healthCheckPromise;
};

const resetNetworkStatus = () => {
  reachabilityCheckVersion += 1;

  networkStatusVar({
    isBackendUnreachable: false,
    retryCount: 0,
  });
};

const updateBackendReachability = async (retryCount: number) => {
  const currentVersion = ++reachabilityCheckVersion;

  if (retryCount < 3) {
    networkStatusVar({
      isBackendUnreachable: false,
      retryCount,
    });
    return;
  }

  const isHealthy = await checkBackendHealth();

  if (currentVersion !== reachabilityCheckVersion) {
    return;
  }

  networkStatusVar({
    isBackendUnreachable: !isHealthy,
    retryCount,
  });
};

const shouldRetryNetworkError = (error: unknown): boolean => {
  const err = error as any;

  const statusCode = err?.statusCode ?? err?.networkError?.statusCode ?? err?.response?.status;
  const graphQLErrors = err?.result?.errors ?? err?.graphQLErrors ?? [];

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    return false;
  }

  if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) {
    return false;
  }

  if (typeof statusCode === 'number' && statusCode >= 500) {
    return true;
  }

  const message = String(err?.message ?? '').toLowerCase();
  const networkErrorMessage = String(err?.networkError?.message ?? '').toLowerCase();
  const combinedMessage = `${message} ${networkErrorMessage}`;

  const looksLikeAbort =
    combinedMessage.includes('aborted') ||
    combinedMessage.includes('aborterror') ||
    combinedMessage.includes('signal is aborted');

  if (looksLikeAbort) {
    return false;
  }

  return true;
};

export const retryLink = new RetryLink({
  delay: (count, operation, error) => {
    import.meta.env.DEV &&
      console.log('[RETRY]:', {
        operation: operation.operationName,
        retryCount: count,
        error,
      });

    if (count <= 3) return 2000;
    if (count <= 10) return 5000;
    if (count <= 20) return 15000;
    return 60 * 1000;
  },

  attempts: (count, operation, error) => {
    if (!error) {
      resetNetworkStatus();
      return false;
    }

    const definition = getMainDefinition(operation.query);

    const isSubscription =
      definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

    const isMutation = definition.kind === 'OperationDefinition' && definition.operation === 'mutation';

    if (isSubscription || isMutation) {
      return false;
    }

    const statusCode = (error as any)?.statusCode ?? (error as any)?.networkError?.statusCode;
    const graphQLErrors = (error as any)?.result?.errors ?? (error as any)?.graphQLErrors ?? [];

    const hasForbiddenGraphQLError =
      Array.isArray(graphQLErrors) &&
      graphQLErrors.some(
        (err: any) =>
          err?.message === 'FORBIDDEN' ||
          err?.message === 'EVENT_IS_NOT_MINE' ||
          err?.extensions?.code === 'FORBIDDEN'
      );

    if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) {
      import.meta.env.DEV &&
        console.warn('[RETRY SKIPPED]: client error', {
          operation: operation.operationName,
          statusCode,
        });

      resetNetworkStatus();
      return false;
    }

    if (hasForbiddenGraphQLError) {
      import.meta.env.DEV &&
        console.warn('[RETRY SKIPPED]: forbidden graphql error', {
          operation: operation.operationName,
          graphQLErrors,
        });

      resetNetworkStatus();
      return false;
    }

    const shouldRetry = shouldRetryNetworkError(error);

    if (!shouldRetry) {
      resetNetworkStatus();
      return false;
    }

    if (count > 20) {
      void updateBackendReachability(count);

      import.meta.env.DEV &&
        console.warn('[RETRY STOPPED]: max retry reached', {
          operation: operation.operationName,
          retryCount: count,
          statusCode,
        });

      return false;
    }

    void updateBackendReachability(count);

    import.meta.env.DEV &&
      console.log('[RETRY ATTEMPT]:', {
        operation: operation.operationName,
        retryCount: count,
        statusCode,
        shouldRetry,
      });

    return true;
  },
});

export const networkRecoveryLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const subscriber = forward(operation).subscribe({
      next: (value) => {
        resetNetworkStatus();
        observer.next(value);
      },
      error: (error) => {
        observer.error(error);
      },
      complete: () => {
        observer.complete();
      },
    });

    return () => subscriber.unsubscribe();
  });
});
